import React from 'react';
import type { InboxEmailListItem as InboxEmailListItemDto } from '../../lib/inboxApi';
import { formatDayMonth as fmtDayMonth } from "../../utils/dateFormat";
import { cleanSenderName as cleanName, initialsForName as initials } from './avatarInitials';

interface Props {
  item: InboxEmailListItemDto;
  selected: boolean;
  onClick: () => void;
}

const COLOR_PALETTE = ['#F97373', '#FACC15', '#22D3EE', '#60A5FA', '#A78BFA', '#F472B6'];

function colorForKey(key: string) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash + key.charCodeAt(i) * 17) % 9973;
  }
  return COLOR_PALETTE[hash % COLOR_PALETTE.length];
}

export const InboxEmailListItem: React.FC<Props> = ({ item, selected, onClick }) => {
  const displayName = cleanName(item.from) || 'Unknown sender';
  const circleColor = colorForKey(item._id || displayName || 'Unknown');
  const subject = (item.subject || '').replace(/\s+/g, ' ').trim() || 'No subject';

  const badgeText = item.inboxLabel || (item.inboxTypes?.[0] || 'Normal');
  const badgeClass = 'bg-slate-100 text-slate-600';
  const aiConfidence =
    typeof item.aiInboxConfidence === 'number'
      ? Math.max(0, Math.min(100, Math.round(item.aiInboxConfidence * 100)))
      : null;
  // Only shown when there is genuinely more than one message. A "1" on every row would be
  // noise, and a row is a conversation whether or not it has replies.
  const messageCount = typeof item.messageCount === 'number' ? item.messageCount : 1;
  const isThread = messageCount > 1;

  return (
    <button
      onClick={onClick}
      className={[
        'w-full text-left px-4 py-3 border-b border-slate-100 flex gap-3 hover:bg-white',
        selected ? 'bg-white shadow-sm' : 'bg-transparent',
      ].join(' ')}
    >
      <div className="flex-shrink-0">
        <div
          className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold text-white"
          style={{ backgroundColor: circleColor }}
        >
          {initials(item.from)}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center">
          <div className="text-sm font-semibold text-slate-800 truncate">{displayName}</div>
          <span className="text-xs text-slate-400 ml-2">
            {fmtDayMonth(item.receivedAt)}
          </span>
        </div>

        <div className="text-xs text-slate-500 mt-0.5 truncate">{subject}</div>

        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-slate-400 truncate max-w-[70%]">
            {item.attachmentCount
              ? `${item.attachmentCount} attachment${item.attachmentCount === 1 ? '' : 's'}`
              : ''}
          </span>

          <div className="flex items-center gap-2">
            {isThread && (
              <span
                className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200"
                title={`${messageCount} messages in this conversation`}
              >
                {messageCount} msgs
              </span>
            )}
            {aiConfidence != null && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-cyan-50 text-cyan-700 border border-cyan-200">
                AI {aiConfidence}%
              </span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badgeClass}`}>
              {badgeText}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};
